// UserDetailTab.jsx
import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
    ClickAwayListener,
    Grow,
    MenuItem,
    Paper,
    Popper,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import { useNavigate } from "react-router-dom";
import { USER_STATUS } from "../../../assets/data/common";
import avatar_2 from "../../../assets/images/logo/avatar-default-logo.png";
import { CustomAdvancedMenuList } from "../../../components/StyledComponents";

import ShowEventIcon14pxSvg from "../../../assets/images/icons/ShowEventIcon14pxSvg";
import CircleXIcon14pxSvg from "../../../assets/images/icons/CircleXIcon14pxSvg";
import RemoveUserIcon from "../../../assets/images/icons/RemoveUserIcon";
import EditNew20pxSvg from "../../../assets/images/icons/EditNew20pxSvg";
import DownloadIcon14pxSvg from "../../../assets/images/icons/DownloadIcon14pxSvg";
import SmileFaceIcon14pxSvg from "../../../assets/images/icons/SmileFaceIcon14pxSvg";
import RemoveUserSvg from "../../../assets/images/icons/terminate.svg";
import { useAuth } from "../../../context/AuthContext";
import CustomSpinner from "../../../components/CustomSpinner";

const UserDetailTab = ({
    info,
    isLoaded,
    loading = false,
    displayInfoData,
    t,

    onEdit,
    onOpenRemoveUser,
    onSetStatusMode,
    onOpenUnregisterDevice,
    groupByUser
}) => {
    const navigate = useNavigate();
    const anchorRefStatus = useRef(null);
    const [openStatus, setOpenStatus] = useState(false);
    const { isAdmin } = useAuth();

    const handleCloseStatus = () => setOpenStatus(false);
    const showLoading = loading || !isLoaded;

    // Tour: mở/đóng status menu (Show event / Terminate / Remove)
    useEffect(() => {
        window.__openUserStatusMenu = () => {
            flushSync(() => setOpenStatus(true));
        };
        window.__closeUserStatusMenu = () => {
            flushSync(() => setOpenStatus(false));
        };
        return () => {
            delete window.__openUserStatusMenu;
            delete window.__closeUserStatusMenu;
        };
    }, []);

    if (showLoading) {
        return (
            <div className="relative w-full" style={{ minHeight: "70.5vh" }}>
                <CustomSpinner loading />
            </div>
        );
    }

    return (
        <>
            {/* ===== Action bar ===== */}
            {isAdmin && (<div className="flex items-center justify-end gap-3 mt-3 mb-3">
                <div className="flex rounded-md border border-[#DBE8FF] overflow-hidden bg-[#F5F8FF] h-10">
                    <button
                        data-tour="user-management-edit-button"
                        onClick={onEdit}
                        className="flex items-center gap-2 px-4 py-2 text-[#0057FF] hover:bg-blue-100 h-[38px]"
                    >
                        <EditNew20pxSvg />
                        {t("button.edit")}
                    </button>

                    <div className="w-[1px] bg-[#DBE8FF]" />

                    <button
                        ref={anchorRefStatus}
                        data-tour="user-management-status-menu-button"
                        onClick={() => setOpenStatus((prev) => !prev)}
                        className="px-3 py-2 hover:bg-blue-100 flex items-center text-[#0057FF]"
                    >
                        <ArrowDropDownIcon />
                    </button>
                </div>
            </div>)}


            {/* ===== Avatar ===== */}
            <div className="flex justify-center">
                <div className="p-1 rounded border border-[#E5E7EB]">
                    <img
                        src={
                            info?.profilePicture
                                ? `data:image/png;base64,${info?.profilePicture}`
                                : avatar_2
                        }
                        alt="avatar"
                        className="w-[128px] h-[128px] object-cover rounded"
                    />
                </div>
            </div>

            {/* ===== Info ===== */}
            <div className="flex flex-col gap-4 mt-6">
                {displayInfoData.map(({ title, value, status }) => {
                    const isTerminate =
                        title === "common.status" &&
                        status === USER_STATUS.terminate;

                    return (
                        <div className={`grid ${isAdmin ? "grid-cols-3" : "grid-cols-3"}`} key={title}>
                            <p className="font-semibold">{t(title)}</p>

                            <div
                                className={` ${isAdmin ? "col-span-2" : "col-span-2"} ${isTerminate ? "text-[#9CA3AF]" : ""
                                    }`}
                            >
                                {typeof value === "string" ? t(value) : value}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ===== Dropdown menu ===== */}
            <Popper
                open={openStatus}
                anchorEl={anchorRefStatus.current}
                placement="bottom-end"
                transition
                disablePortal
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps} timeout={window.__tourIsOpen ? 0 : undefined}>
                        <Paper sx={{ borderRadius: 1, p: 0, mt: 1 }}>
                            <ClickAwayListener
                                onClickAway={() => {
                                    if (window.__tourIsOpen) return;
                                    handleCloseStatus();
                                }}
                            >
                                <CustomAdvancedMenuList>
                                    <MenuItem
                                        data-tour="user-management-show-event-button"
                                        className="gap-3 items-center h-9 hover:!bg-blue-100"
                                        onClick={() => {
                                            handleCloseStatus();
                                            navigate("/controlpanel/event-log-management", {
                                                state: { userId: info.userId },
                                            });
                                        }}
                                    >
                                        <ShowEventIcon14pxSvg />
                                        {t("button.show-event")}
                                    </MenuItem>

                                    <MenuItem disabled className="gap-3 items-center h-9 hover:!bg-blue-100">
                                        <SmileFaceIcon14pxSvg />
                                        {t("button.biometric")}
                                    </MenuItem>

                                    {(info.active === USER_STATUS.unblock ||
                                        info.active === USER_STATUS.terminate) && (
                                            <MenuItem className="gap-3 items-center h-9 hover:!bg-blue-100"
                                                disabled
                                                onClick={() => {
                                                    handleCloseStatus();
                                                    onSetStatusMode(USER_STATUS.block);
                                                }}
                                            >
                                                <CircleXIcon14pxSvg />
                                                {t("button.block")}
                                            </MenuItem>
                                        )}

                                    {(info.active === USER_STATUS.block ||
                                        info.active === USER_STATUS.terminate) && (
                                            <MenuItem className="gap-3 items-center h-9 hover:!bg-blue-100"
                                                onClick={() => {
                                                    handleCloseStatus();
                                                    onSetStatusMode(USER_STATUS.unblock);
                                                }}
                                            >
                                                <CheckIcon fontSize="small" />
                                                {t("button.active")}
                                            </MenuItem>
                                        )}

                                    {info.active !== USER_STATUS.terminate &&
                                        info.active !== USER_STATUS.pendingActivation && (
                                        <MenuItem
                                            data-tour="user-management-terminate-button"
                                            className="gap-3 items-center h-9 hover:!bg-blue-100"
                                            disabled={info?.owner}
                                            onClick={() => {
                                                handleCloseStatus();
                                                onSetStatusMode(USER_STATUS.terminate);
                                            }}
                                        >
                                            <img src={RemoveUserSvg} alt="" className="w-4 h-4" />
                                            {t("common.content-150")}
                                        </MenuItem>
                                    )}

                                    <MenuItem className="gap-3 items-center h-9 hover:!bg-blue-100" disabled>
                                        <DownloadIcon14pxSvg />
                                        {t("button.export")}
                                    </MenuItem>

                                    <MenuItem
                                        data-tour="user-management-remove-button"
                                        disabled={info?.owner}
                                        className="gap-3 items-center h-9  !text-red-500"
                                        onClick={() => {
                                            handleCloseStatus();
                                            onOpenRemoveUser();
                                        }}
                                    >
                                        <RemoveUserIcon />
                                        {t("button.remove")}
                                    </MenuItem>

                                </CustomAdvancedMenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};

export default UserDetailTab;
